import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import { Box, Typography, Button, IconButton, Chip, CircularProgress } from '@mui/material'
import { Close, Check } from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import { startCheckout } from '@/lib/payments'

// ==================== STYLED COMPONENTS ====================
const ModalOverlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  backdropFilter: 'blur(12px)',
  zIndex: 9999,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '40px 20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})

const ModalContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  maxWidth: '1100px',
  background: '#1d1d1d',
  borderRadius: '16px',
  padding: '48px 40px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
})

const LimitedTimeBanner = styled(Box)({
  position: 'absolute',
  top: '-16px',
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
  color: '#ffffff',
  fontSize: '13px',
  fontWeight: 600,
  padding: '8px 20px',
  borderRadius: '8px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 24px rgba(139, 92, 246, 0.4)',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '&::before': {
    content: '"🔥"',
    fontSize: '16px',
  },
})

const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: '20px',
  right: '20px',
  color: 'rgba(255, 255, 255, 0.6)',
  transition: 'all 0.2s',
  '&:hover': {
    color: '#ffffff',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
})

const Header = styled(Box)({
  textAlign: 'center',
  marginBottom: '40px',
})

const Title = styled(Typography)({
  fontSize: '32px',
  fontWeight: 700,
  color: '#ffffff',
  marginBottom: '8px',
  letterSpacing: '-0.02em',
})

const Subtitle = styled(Typography)({
  fontSize: '15px',
  color: 'rgba(255, 255, 255, 0.5)',
  fontWeight: 400,
})

const ToggleContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  marginBottom: '40px',
})

const ToggleButton = styled(Button)<{ active?: boolean }>(({ active }) => ({
  padding: '8px 20px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  color: active ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
  backgroundColor: active ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
  border: 'none',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: active ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.04)',
  },
}))

const PricingGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '24px',
  marginBottom: '48px',
  '@media (max-width: 900px)': {
    gridTemplateColumns: '1fr',
    gap: '20px',
  },
})

const PricingCard = styled(Box)<{ featured?: boolean }>(({ featured }) => ({
  position: 'relative',
  background: featured ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.02)',
  borderRadius: '12px',
  padding: '32px 24px',
  border: featured ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(255, 255, 255, 0.06)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    borderColor: featured ? 'rgba(139, 92, 246, 0.5)' : 'rgba(255, 255, 255, 0.12)',
    transform: 'translateY(-4px)',
    boxShadow: featured ? '0 12px 40px rgba(139, 92, 246, 0.15)' : '0 8px 32px rgba(0, 0, 0, 0.3)',
  },
}))

const BestValueBadge = styled(Box)({
  position: 'absolute',
  top: '-12px',
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
  color: '#ffffff',
  fontSize: '12px',
  fontWeight: 600,
  padding: '4px 12px',
  borderRadius: '6px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
})

const PlanName = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.6)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '12px',
})

const PriceContainer = styled(Box)({
  marginBottom: '24px',
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
})

const OldPrice = styled(Typography)({
  fontSize: '24px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.3)',
  textDecoration: 'line-through',
})

const Price = styled(Typography)({
  fontSize: '40px',
  fontWeight: 700,
  color: '#ffffff',
  letterSpacing: '-0.02em',
})

const PriceUnit = styled('span')({
  fontSize: '16px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.5)',
  marginLeft: '4px',
})

const FeatureList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginBottom: '32px',
  flex: 1,
})

const FeatureItem = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '14px',
  lineHeight: 1.5,
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    color: '#8b5cf6',
    flexShrink: 0,
    marginTop: '2px',
  },
})

const InfoIcon = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '14px',
  height: '14px',
  borderRadius: '50%',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  fontSize: '10px',
  color: 'rgba(255, 255, 255, 0.4)',
  marginLeft: '4px',
  cursor: 'help',
})

const PlanButton = styled(Button)<{ iscurrent?: string; isfeatured?: string }>(
  ({ iscurrent, isfeatured }) => ({
    width: '100%',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.2s',
    ...(iscurrent === 'true'
      ? {
          color: 'rgba(255, 255, 255, 0.9) !important',
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          cursor: 'default',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
          },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.9) !important',
          },
        }
      : isfeatured === 'true'
      ? {
          color: '#ffffff',
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
          border: 'none',
          '&:hover': {
            background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(139, 92, 246, 0.4)',
          },
        }
      : {
          color: '#ffffff',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            borderColor: 'rgba(255, 255, 255, 0.2)',
          },
        }),
  }),
)

const FooterNote = styled(Typography)({
  fontSize: '12px',
  color: 'rgba(255, 255, 255, 0.4)',
  textAlign: 'center',
  marginTop: '8px',
})

const ComparisonSection = styled(Box)({
  marginTop: '48px',
  paddingTop: '48px',
  borderTop: '1px solid rgba(255, 255, 255, 0.06)',
})

const ComparisonTitle = styled(Typography)({
  fontSize: '24px',
  fontWeight: 700,
  color: '#ffffff',
  textAlign: 'center',
  marginBottom: '32px',
  letterSpacing: '-0.02em',
})

const ComparisonTable = styled(Box)({
  background: 'rgba(255, 255, 255, 0.02)',
  borderRadius: '12px',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  overflow: 'hidden',
})

const TableRow = styled(Box)<{ header?: boolean }>(({ header }) => ({
  display: 'grid',
  gridTemplateColumns: '2fr 1fr 1fr 1fr',
  alignItems: 'center',
  padding: '16px 24px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
  background: header ? 'rgba(255, 255, 255, 0.03)' : 'transparent',
  '&:last-child': {
    borderBottom: 'none',
  },
  '@media (max-width: 900px)': {
    gridTemplateColumns: '1.5fr 1fr 1fr 1fr',
    padding: '12px 16px',
  },
}))

const TableCell = styled(Box)<{ header?: boolean }>(({ header }) => ({
  fontSize: header ? '13px' : '14px',
  fontWeight: header ? 700 : 400,
  color: header ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.7)',
  textTransform: header ? 'uppercase' : 'none',
  letterSpacing: header ? '0.05em' : 'normal',
  textAlign: 'center',
  '&:first-of-type': {
    textAlign: 'left',
  },
}))

const CategoryTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  color: '#ffffff',
  padding: '20px 24px 12px',
  background: 'rgba(139, 92, 246, 0.05)',
})

// ==================== INTERFACES ====================
interface PricingModalProps {
  open:     boolean
  onClose:  () => void
  userId?:  string
  userEmail?: string
}

interface PricingTier {
  name: string
  monthlyPrice: number
  yearlyPrice: number
  oldMonthlyPrice?: number
  features: Array<{ text: string; info?: string }>
  cta: string
  featured?: boolean
  current?: boolean
}

interface Feature {
  category: string
  items: {
    name: string
    free: boolean
    pro: boolean
    enterprise: boolean
  }[]
}

// ==================== DATA ====================
// Keepsake / Gallery / Studio — same plans as PricingPage.tsx and
// payments-infra's app-registry.ts for 'egowall' (Gallery→pro,
// Studio→enterprise). Keep these three in sync if pricing changes.
const pricingTiers: PricingTier[] = [
  {
    name: 'Keepsake',
    monthlyPrice: 0,
    yearlyPrice: 0,
    features: [
      { text: '3 albums, unlimited viewers' },
      { text: 'Full resolution — always' },
      { text: 'Reactions, comments & pins' },
      { text: '1 share key per album + QR' },
    ],
    cta: 'Current Plan',
    current: true,
  },
  {
    name: 'Gallery',
    monthlyPrice: 9,
    yearlyPrice: 7.5,
    features: [
      { text: 'Unlimited albums & unlimited keys' },
      { text: '3D galleries — 14 rooms, all finishes' },
      { text: 'Guest uploads with host review' },
      { text: 'Print-ready QR key cards' },
      { text: 'Voice notes & live presence' },
    ],
    cta: 'Upgrade to Gallery',
    featured: true,
  },
  {
    name: 'Studio',
    monthlyPrice: 24,
    yearlyPrice: 20,
    features: [
      { text: 'Everything in Gallery' },
      { text: 'Client workspaces & handoff' },
      { text: 'Your logo on albums & key cards' },
      { text: 'RAW proofing & downloads' },
      { text: 'Priority support' },
    ],
    cta: 'Upgrade to Studio',
  },
]

const featureComparison: Feature[] = [
  {
    category: 'Albums & sharing',
    items: [
      { name: 'Albums', free: true, pro: true, enterprise: true },
      { name: 'Unlimited albums', free: false, pro: true, enterprise: true },
      { name: 'Guest uploads', free: false, pro: true, enterprise: true },
      { name: '3D galleries', free: false, pro: true, enterprise: true },
    ],
  },
  {
    category: 'Quality & branding',
    items: [
      { name: 'Full resolution', free: true, pro: true, enterprise: true },
      { name: 'RAW proofing & downloads', free: false, pro: false, enterprise: true },
      { name: 'Your logo on albums & keys', free: false, pro: false, enterprise: true },
    ],
  },
  {
    category: 'Collaboration & support',
    items: [
      { name: 'Community support', free: true, pro: true, enterprise: true },
      { name: 'Priority support', free: false, pro: false, enterprise: true },
      { name: 'Client workspaces & handoff', free: false, pro: false, enterprise: true },
    ],
  },
]

// ==================== COMPONENT ====================
export const PricingModal: React.FC<PricingModalProps> = ({ open, onClose, userId, userEmail }) => {
  const [isYearly, setIsYearly] = useState(false)
  const [loadingTier, setLoadingTier] = useState<string | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)

  // Display names (Gallery/Studio) → payments-infra's generic tier names.
  const TIER_BY_NAME: Record<string, 'pro' | 'enterprise'> = { gallery: 'pro', studio: 'enterprise' }

  const handleUpgrade = async (tierName: string) => {
    if (!userId) return
    const tier = TIER_BY_NAME[tierName.toLowerCase()]
    if (!tier) return

    setLoadingTier(tierName)
    setCheckoutError(null)
    try {
      await startCheckout(userId, userEmail, tier, isYearly ? 'yearly' : 'monthly')
      // startCheckout redirects — execution won't reach here on success
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'Failed to start checkout')
      setLoadingTier(null)
    }
  }

  if (!open) return null

  return ReactDOM.createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <LimitedTimeBanner>Limited Time: Get 2 months free with annual plan</LimitedTimeBanner>

        <CloseButton onClick={onClose}>
          <Close />
        </CloseButton>

        <Header>
          <Title>Choose your plan</Title>
          <Subtitle>Unlock unlimited creativity and premium features</Subtitle>
        </Header>

        <ToggleContainer>
          <ToggleButton active={!isYearly} onClick={() => setIsYearly(false)}>
            Monthly
          </ToggleButton>
          <ToggleButton active={isYearly} onClick={() => setIsYearly(true)}>
            Annually
          </ToggleButton>
        </ToggleContainer>

        <PricingGrid>
          {pricingTiers.map((tier) => (
            <PricingCard key={tier.name} featured={tier.featured}>
              {tier.featured && <BestValueBadge>Best Value</BestValueBadge>}

              <PlanName>{tier.name}</PlanName>

              <PriceContainer>
                {tier.oldMonthlyPrice && <OldPrice>${tier.oldMonthlyPrice}</OldPrice>}
                <Box>
                  <Price>
                    ${isYearly ? tier.yearlyPrice : tier.monthlyPrice}
                    <PriceUnit>/month</PriceUnit>
                  </Price>
                </Box>
              </PriceContainer>

              <FeatureList>
                {tier.features.map((feature, index) => (
                  <FeatureItem key={index}>
                    <Check />
                    <span>
                      {feature.text}
                      {feature.info && <InfoIcon title={feature.info}>i</InfoIcon>}
                    </span>
                  </FeatureItem>
                ))}
              </FeatureList>

              <Box>
                <PlanButton
                  iscurrent={tier.current ? 'true' : 'false'}
                  isfeatured={tier.featured ? 'true' : 'false'}
                  onClick={() => !tier.current && handleUpgrade(tier.name)}
                  disabled={tier.current || loadingTier !== null}
                >
                  {loadingTier === tier.name ? (
                    <CircularProgress size={18} color='inherit' />
                  ) : tier.cta}
                </PlanButton>
                <FooterNote>cancel anytime • 100% content ownership</FooterNote>
              </Box>
            </PricingCard>
          ))}
        </PricingGrid>

        {checkoutError && (
          <Box sx={{ textAlign: 'center', mb: 3, color: '#f87171', fontSize: '14px' }}>
            {checkoutError}
          </Box>
        )}

        <ComparisonSection>
          <ComparisonTitle>Compare all features</ComparisonTitle>
          <ComparisonTable>
            <TableRow header>
              <TableCell header>Features</TableCell>
              <TableCell header>Free</TableCell>
              <TableCell header>Pro</TableCell>
              <TableCell header>Enterprise</TableCell>
            </TableRow>

            {featureComparison.map((category) => (
              <React.Fragment key={category.category}>
                <CategoryTitle>{category.category}</CategoryTitle>
                {category.items.map((item) => (
                  <TableRow key={item.name}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>
                      {item.free ? (
                        <Check sx={{ fontSize: 20, color: '#8b5cf6' }} />
                      ) : (
                        <Box sx={{ color: 'rgba(255, 255, 255, 0.2)' }}>—</Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.pro ? (
                        <Check sx={{ fontSize: 20, color: '#8b5cf6' }} />
                      ) : (
                        <Box sx={{ color: 'rgba(255, 255, 255, 0.2)' }}>—</Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.enterprise ? (
                        <Check sx={{ fontSize: 20, color: '#8b5cf6' }} />
                      ) : (
                        <Box sx={{ color: 'rgba(255, 255, 255, 0.2)' }}>—</Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </ComparisonTable>
        </ComparisonSection>
      </ModalContainer>
    </ModalOverlay>,
    document.body,
  )
}

export default PricingModal
